/**
 * useProductUpdatesScan (Phase 86, D-14): the Product updates worklist.
 *
 * Walks `productUpdates.getPage` serially on the generic
 * `useSerialCursorScan` (one page in flight, each page cached for 5 minutes)
 * and folds every page into ONE list. There is no pager: the tab shows the
 * whole folded list and its count, so filtering, counting and ordering all
 * happen here, in one layer, over the folded array (CLAUDE.md section 10).
 *
 * - Deduped by `productId`: the server walks one store product type at a time
 *   and keeps a product only under its exact type, but a second page can still
 *   repeat a product, and the count must not.
 * - Sorted by title with `localeCompare` at sensitivity base, so case and
 *   accents do not split the order.
 * - A rescan keeps the previous rows on screen until the new walk folds its
 *   first page (the tab shows its header spinner meanwhile, never the loader).
 *
 * Phase 88: every page also says whether the org is a composition org and
 * where its store SKU switch is. The fold keeps the LATEST page's answer
 * (null until a page lands), so the tab can tell "nothing needs files" from
 * "standard products are not set up yet".
 *
 * BLU-1238 (R3-2): products that keep printing their current files (an
 * active skip platen mark) are left out by the server unless `includeKept`
 * asks for them; that flag is a query input on every page, part of the walk's
 * identity, so toggling it restarts the walk from the first page. Each page's
 * `keptProducts` is summed over the same walk (a product repeated on a second
 * page is counted twice: the server rarely repeats one, and the count is a
 * hint, never a gate).
 *
 * Budget: the page gates `enabled` on the tab being open, so the walk starts
 * when the tab is first opened, not on every product sync page load. The fold
 * stays in state while the tab is closed, so the trigger keeps its count.
 */
import { useMemo, useState } from 'react';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import { PAGE_STALE_MS, useSerialCursorScan } from './useSerialCursorScan';

export type ProductUpdatesPage = RouterOutputs['productUpdates']['getPage'];
export type ProductUpdateRow = ProductUpdatesPage['products'][number];
export type ProductUpdateNeed = ProductUpdateRow['needs'][number];

export interface ProductUpdatesScanState {
  /** Every product still owing platen variant files, folded so far, sorted by title. */
  rows: ProductUpdateRow[];
  /**
   * The worklist's own answer to "does this org have platen size rules"
   * (`rulesConfigured` on its first page), or null until a page has landed.
   * The page hides the tab when it is false: with no rules nothing can ever
   * need a platen variant file.
   */
  rulesConfigured: boolean | null;
  /** Phase 88: the latest page's `compositionOrg`; null until a page has landed. */
  compositionOrg: boolean | null;
  /** Phase 88: the latest page's store SKU switch; null until a page has landed. */
  declaredMode: ProductUpdatesPage['declaredMode'] | null;
  /** `rows.length`: the count the tab trigger and list header show. */
  count: number;
  /** True once a walk has folded at least one page (an empty list is then a real answer). */
  hasLoaded: boolean;
  /** True once the server reports no further pages: `rows` is then the whole worklist. */
  isComplete: boolean;
  /** True while a page request is in flight. */
  isScanning: boolean;
  /** Set when a page request failed; the walk stops and keeps what it folded. */
  error: string | null;
  /** Re-read the worklist from the first page, re-hitting the server. */
  rescan: () => void;
  /** BLU-1238: products that keep printing their current files, summed over the walk. */
  keptCount: number;
  /** BLU-1238: whether this walk lists the kept products too. */
  includeKept: boolean;
  /** BLU-1238: list (or leave out) the kept products; restarts the walk. */
  setIncludeKept: (include: boolean) => void;
}

interface Acc {
  rows: Map<string, ProductUpdateRow>;
  rulesConfigured: boolean | null;
  compositionOrg: boolean | null;
  declaredMode: ProductUpdatesPage['declaredMode'] | null;
  kept: number;
}

interface Folded {
  rows: ProductUpdateRow[];
  rulesConfigured: boolean | null;
  compositionOrg: boolean | null;
  declaredMode: ProductUpdatesPage['declaredMode'] | null;
  kept: number;
}

const byTitle = (a: ProductUpdateRow, b: ProductUpdateRow): number =>
  a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }) || a.productId.localeCompare(b.productId);

/**
 * @param enabled Gate the walk: true only while the Product updates tab is open.
 */
export function useProductUpdatesScan(enabled: boolean): ProductUpdatesScanState {
  const utils = trpc.useUtils();
  const [includeKept, setIncludeKept] = useState(false);

  const scan = useSerialCursorScan<ProductUpdatesPage, Acc, Folded>({
    enabled,
    // Own-org read whose one filter input is includeKept: a toggle starts a
    // fresh walk (a cursor from one walk is never reused for the other), as
    // does the internal generation (rescan).
    identity: includeKept ? 'product-updates:kept' : 'product-updates',
    fetchPage: (cursor) =>
      utils.productUpdates.getPage.fetch(includeKept ? { cursor, includeKept: true } : { cursor }, { staleTime: PAGE_STALE_MS }),
    invalidate: () => utils.productUpdates.getPage.invalidate(),
    createAcc: () => ({ rows: new Map(), rulesConfigured: null, compositionOrg: null, declaredMode: null, kept: 0 }),
    fold: (acc, page) => {
      // Every page says it; the walk's first page answers for the walk.
      acc.rulesConfigured ??= page.rulesConfigured;
      // The switch can change mid-walk; the latest page is the freshest answer.
      acc.compositionOrg = page.compositionOrg;
      acc.declaredMode = page.declaredMode;
      acc.kept += page.keptProducts;
      // First sighting wins: a repeat of the same product carries the same
      // needs from the same file-state read shape.
      for (const row of page.products) if (!acc.rows.has(row.productId)) acc.rows.set(row.productId, row);
    },
    finalize: (acc) => ({
      rows: [...acc.rows.values()].sort(byTitle),
      rulesConfigured: acc.rulesConfigured,
      compositionOrg: acc.compositionOrg,
      declaredMode: acc.declaredMode,
      kept: acc.kept,
    }),
    keepPreviousResult: true,
  });

  const { result, isComplete, isScanning, error, rescan } = scan;
  return useMemo(() => {
    const rows = result?.rows ?? [];
    return {
      rows,
      rulesConfigured: result?.rulesConfigured ?? null,
      compositionOrg: result?.compositionOrg ?? null,
      declaredMode: result?.declaredMode ?? null,
      count: rows.length,
      hasLoaded: result !== null,
      isComplete,
      isScanning,
      error,
      rescan,
      keptCount: result?.kept ?? 0,
      includeKept,
      setIncludeKept,
    };
  }, [result, isComplete, isScanning, error, rescan, includeKept]);
}
